// Auto-extracted tab: point-in-time
import React, { useState, useEffect, useMemo, useCallback } from 'react';

/**
 * PointInTimeTab - extracted from app.jsx
 * Original lines: 8190-8555
 * 
 * TODO: This component needs props passed from AppContent:
 * - darkMode, scenarios, thresholds, etc.
 * - All the state and handlers it uses
 */
export function PointInTimeTab(props) {
  // Original render content (needs refactoring to use props):
  return (
    <>
          <div className="space-y-6">
            {/* Examiner-Friendly Banner */}
            <div className="bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 border border-slate-200 rounded-2xl p-5">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center shadow-lg shadow-blue-500/20 flex-shrink-0">
                  <Icons.Clock className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-blue-900">Point-in-Time Reconstruction</h3>
                  <p className="text-sm text-blue-700 mt-1">
                    Select any historical date to view the exact state of governance objects as they existed on that date.
                    Perfect for examiner requests like "Show me your configuration as of March 15, 2024."
                  </p>
                </div>
              </div>
            </div>
            
            {/* Date Picker and Load Button */}
            <Card darkMode={darkMode} title="Select Date" subtitle="Choose a date to reconstruct the governance state">
              <div className="space-y-4">
                {/* Quick Date Presets */}
                <div className="flex flex-wrap gap-2">
                  <span className="text-sm text-slate-500 mr-2">Quick select:</span>
                  <button
                    className="px-3 py-1 text-xs bg-slate-100 hover:bg-slate-200 rounded-lg transition"
                    onClick={() => {
                      const d = new Date();
                      d.setMonth(d.getMonth() - 1);
                      setPitDate(d.toISOString().slice(0, 10));
                    }}
                  >
                    Last Month
                  </button>
                  <button
                    className="px-3 py-1 text-xs bg-slate-100 hover:bg-slate-200 rounded-lg transition"
                    onClick={() => {
                      const d = new Date();
                      d.setMonth(d.getMonth() - 3);
                      setPitDate(d.toISOString().slice(0, 10));
                    }}
                  >
                    Last Quarter
                  </button>
                  <button
                    className="px-3 py-1 text-xs bg-slate-100 hover:bg-slate-200 rounded-lg transition"
                    onClick={() => {
                      const d = new Date();
                      d.setFullYear(d.getFullYear() - 1);
                      setPitDate(d.toISOString().slice(0, 10));
                    }}
                  >
                    1 Year Ago
                  </button>
                  <button
                    className="px-3 py-1 text-xs bg-slate-100 hover:bg-slate-200 rounded-lg transition"
                    onClick={() => {
                      const d = new Date();
                      d.setFullYear(d.getFullYear() - 2);
                      setPitDate(d.toISOString().slice(0, 10));
                    }}
                  >
                    2 Years Ago
                  </button>
                  {attestations[0] && (
                    <button
                      className="px-3 py-1 text-xs bg-emerald-100 hover:bg-emerald-200 text-emerald-700 rounded-lg transition"
                      onClick={() => {
                        setPitDate(attestations[0].attested_at?.slice(0, 10) || new Date().toISOString().slice(0, 10));
                      }}
                    >
                      Last Attestation ({formatDate(attestations[0].attested_at)})
                    </button>
                  )}
                </div>
                
                <div className="flex items-end gap-4">
                  <div className="flex-1 max-w-xs">
                    <label htmlFor="pit-date" className="block text-sm font-medium text-slate-700 mb-1">As-of Date</label>
                    <input
                      id="pit-date"
                      type="date"
                      className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                      value={pitDate}
                      onChange={e => setPitDate(e.target.value)}
                      max={new Date().toISOString().slice(0, 10)}
                    />
                  </div>
                  <Button
                    variant="primary"
                    onClick={() => loadPointInTime(pitDate)}
                    disabled={pitLoading}
                  >
                    {pitLoading ? (
                      <>
                        <Icons.Clock className="animate-spin" /> Loading...
                      </>
                    ) : (
                      <>
                        <Icons.Clock /> Reconstruct State
                      </>
                    )}
                  </Button>
                </div>
              </div>
              
              {/* Attestation Status for Selected Date */}
              {pitLoaded && (() => {
                const attestation = getPitAttestationStatus(pitDate);
                return (
                  <div className={`p-4 rounded-lg mb-4 ${
                    attestation ? "bg-emerald-50 border border-emerald-200" : "bg-amber-50 border border-amber-200"
                  }`}>
                    <div className="flex items-center gap-3">
                      {attestation ? (
                        <>
                          <Icons.Check className="text-emerald-600" />
                          <div>
                            <div className="font-medium text-emerald-900">
                              Attested for {new Date(pitDate).toLocaleDateString("en-US", { month: "long", year: "numeric" })}
                            </div>
                            <div className="text-sm text-emerald-700">
                              Attested on {formatDate(attestation.attested_at)} • {attestation.scenario_count} scenarios, {attestation.threshold_count} thresholds
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <Icons.Alert className="text-amber-600" />
                          <div>
                            <div className="font-medium text-amber-900">
                              No attestation for {new Date(pitDate).toLocaleDateString("en-US", { month: "long", year: "numeric" })}
                            </div>
                            <div className="text-sm text-amber-700">
                              This period was not attested or attestation record not found
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })()}
              
              {/* Summary Stats */}
              {pitLoaded && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-slate-50 rounded-lg p-3">
                    <div className="text-2xl font-bold text-slate-900">{pitScenarios.length}</div>
                    <div className="text-sm text-slate-500">Scenarios</div>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <div className="text-2xl font-bold text-slate-900">{pitThresholds.length}</div>
                    <div className="text-sm text-slate-500">Thresholds</div>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <div className="text-2xl font-bold text-emerald-600">
                      {[...pitScenarios, ...pitThresholds].filter(i => calcEQScore(i) >= 8).length}
                    </div>
                    <div className="text-sm text-slate-500">Complete (9/9)</div>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <div className="text-2xl font-bold text-amber-600">
                      {[...pitScenarios, ...pitThresholds].filter(i => calcEQScore(i) < 8).length}
                    </div>
                    <div className="text-sm text-slate-500">Incomplete</div>
                  </div>
                </div>
              )}
              
              {/* Changes Since Date - Examiner View */}
              {pitLoaded && (() => {
                const pitDateObj = new Date(pitDate);
                const addedScenarios = scenarios.filter(s => new Date(s.created_at) > pitDateObj);
                const addedThresholds = thresholds.filter(t => new Date(t.created_at) > pitDateObj);
                const modifiedScenarios = scenarios.filter(s => 
                  new Date(s.updated_at || s.created_at) > pitDateObj && 
                  new Date(s.created_at) <= pitDateObj
                );
                const modifiedThresholds = thresholds.filter(t => 
                  new Date(t.updated_at || t.created_at) > pitDateObj && 
                  new Date(t.created_at) <= pitDateObj
                );
                const totalChanges = addedScenarios.length + addedThresholds.length + modifiedScenarios.length + modifiedThresholds.length;
                
                if (totalChanges === 0) return null;
                
                return (
                  <div className="mt-6 border-t border-slate-200 pt-6">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <span className="text-blue-500">📝</span>
                      Changes Since {formatDate(pitDate)}
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-200">
                        <div className="text-xl font-bold text-emerald-600">{addedScenarios.length}</div>
                        <div className="text-xs text-emerald-700">New Scenarios</div>
                      </div>
                      <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-200">
                        <div className="text-xl font-bold text-emerald-600">{addedThresholds.length}</div>
                        <div className="text-xs text-emerald-700">New Thresholds</div>
                      </div>
                      <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                        <div className="text-xl font-bold text-amber-600">{modifiedScenarios.length}</div>
                        <div className="text-xs text-amber-700">Modified Scenarios</div>
                      </div>
                      <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                        <div className="text-xl font-bold text-amber-600">{modifiedThresholds.length}</div>
                        <div className="text-xs text-amber-700">Modified Thresholds</div>
                      </div>
                    </div>
                    
                    {/* List of changes */}
                    {(addedScenarios.length > 0 || modifiedScenarios.length > 0) && (
                      <div className="bg-slate-50 rounded-lg p-4 mb-3">
                        <h4 className="font-medium text-slate-700 mb-2">Scenario Changes</h4>
                        <ul className="text-sm space-y-1">
                          {addedScenarios.slice(0, 5).map(s => (
                            <li key={s.id} className="flex items-center gap-2">
                              <span className="text-emerald-500">+</span>
                              <span className="font-medium">{s.name}</span>
                              <span className="text-slate-500">added {formatDate(s.created_at)}</span>
                            </li>
                          ))}
                          {modifiedScenarios.slice(0, 5).map(s => (
                            <li key={s.id} className="flex items-center gap-2">
                              <span className="text-amber-500">~</span>
                              <span className="font-medium">{s.name}</span>
                              <span className="text-slate-500">modified {formatDate(s.updated_at || s.created_at)}</span>
                            </li>
                          ))}
                          {(addedScenarios.length + modifiedScenarios.length) > 10 && (
                            <li className="text-slate-500">...and {addedScenarios.length + modifiedScenarios.length - 10} more</li>
                          )}
                        </ul>
                      </div>
                    )}
                    
                    {(addedThresholds.length > 0 || modifiedThresholds.length > 0) && (
                      <div className="bg-slate-50 rounded-lg p-4">
                        <h4 className="font-medium text-slate-700 mb-2">Threshold Changes</h4>
                        <ul className="text-sm space-y-1">
                          {addedThresholds.slice(0, 5).map(t => (
                            <li key={t.id} className="flex items-center gap-2">
                              <span className="text-emerald-500">+</span>
                              <span className="font-medium">{t.name}</span>
                              <span className="text-slate-500">added {formatDate(t.created_at)}</span>
                            </li>
                          ))}
                          {modifiedThresholds.slice(0, 5).map(t => (
                            <li key={t.id} className="flex items-center gap-2">
                              <span className="text-amber-500">~</span>
                              <span className="font-medium">{t.name}</span>
                              <span className="text-slate-500">modified {formatDate(t.updated_at || t.created_at)}</span>
                            </li>
                          ))}
                          {(addedThresholds.length + modifiedThresholds.length) > 10 && (
                            <li className="text-slate-500">...and {addedThresholds.length + modifiedThresholds.length - 10} more</li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })()}
            </Card>
            
            {/* Scenarios as of Date */}
            {pitLoaded && (
              <Card darkMode={darkMode} title={`Scenarios as of ${formatDate(pitDate)}`} subtitle={`${pitScenarios.length} scenario${pitScenarios.length !== 1 ? "s" : ""}`}>
                {pitScenarios.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    No scenarios existed as of this date
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gradient-to-r from-slate-50 to-slate-100/80">
                        <tr>
                          <th className="px-4 py-3.5 text-left font-semibold text-slate-700">Name</th>
                          <th className="px-4 py-3.5 text-left font-semibold text-slate-700">Risk</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-600">Owner</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-600">EQ Score</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-600">Valid From</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-600">Regulatory Basis</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 bg-white">
                        {pitScenarios.map(s => (
                          <tr key={s.id} className="hover:bg-blue-50/50 transition-colors">
                            <td className="px-4 py-3.5 font-medium text-slate-900">{s.name}</td>
                            <td className="px-4 py-3.5">
                              <Badge color={s.risk_level === "high" ? "red" : s.risk_level === "low" ? "emerald" : "amber"}>
                                {s.risk_level || "medium"}
                              </Badge>
                            </td>
                            <td className="px-3 py-3 text-slate-500">{s.owner_email || "—"}</td>
                            <td className="px-3 py-3">
                              <EQScoreRing score={calcEQScore(s)} size={36} />
                            </td>
                            <td className="px-3 py-3 text-slate-500">{formatDate(s.valid_from)}</td>
                            <td className="px-3 py-3 text-slate-500 max-w-xs truncate">{s.regulatory_basis || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            )}
            
            {/* Thresholds as of Date */}
            {pitLoaded && (
              <Card darkMode={darkMode} title={`Thresholds as of ${formatDate(pitDate)}`} subtitle={`${pitThresholds.length} threshold${pitThresholds.length !== 1 ? "s" : ""}`}>
                {pitThresholds.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    No thresholds existed as of this date
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gradient-to-r from-slate-50 to-slate-100/80">
                        <tr>
                          <th className="px-4 py-3.5 text-left font-semibold text-slate-700">Name</th>
                          <th className="px-4 py-3.5 text-left font-semibold text-slate-700">Value</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-600">Risk</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-600">Owner</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-600">EQ Score</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-600">Valid From</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 bg-white">
                        {pitThresholds.map(t => (
                          <tr key={t.id} className="hover:bg-blue-50/50 transition-colors">
                            <td className="px-4 py-3.5 font-medium text-slate-900">{t.name}</td>
                            <td className="px-4 py-3.5 text-slate-700">
                              {t.current_value != null ? `${t.current_value} ${t.unit || ""}`.trim() : "—"}
                            </td>
                            <td className="px-3 py-3">
                              <Badge color={t.risk_level === "high" ? "red" : t.risk_level === "low" ? "emerald" : "amber"}>
                                {t.risk_level || "medium"}
                              </Badge>
                            </td>
                            <td className="px-3 py-3 text-slate-500">{t.owner_email || "—"}</td>
                            <td className="px-3 py-3">
                              <EQScoreRing score={calcEQScore(t)} size={36} />
                            </td>
                            <td className="px-3 py-3 text-slate-500">{formatDate(t.valid_from)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            )}
            
            {/* Instructions when not loaded */}
            {!pitLoaded && !pitLoading && (
              <div className="text-center py-12 text-slate-500">
                <Icons.Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Select a date and click "Reconstruct State" to view governance objects as they existed at that point in time.</p>
              </div>
            )}
          </div>
    </>
  );
}

export default PointInTimeTab;
